// Operations -> DISA Randoms (George 2026-11-01). The monthly DISA random selection
// lists from the Outlook OccMed folder, mirrored by the MinisForum OccMed saver, and
// whether each selected person's test came in: matched from the "DISA Test Result
// Certificate" emails (Test Purpose = Random) collected inside the selection period.
// Admin and leadership only; the server enforces it, the tab is hidden to match.
// Report fields and verdicts are read-only. Nothing is marked tested by hand: SSS
// sends the person to DISA on an authorization form generated in DISAWorks, so the
// tab logs the form (date sent + authorization #), Disposition and Notes. Forms the
// saver finds in George's Downloads fill in on their own; the inputs are the fallback.

import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate, fmtDateTime, chipBtn } from './UI';
import BlurDateInput from './BlurDateInput';

const STATUS_COLORS = {
  Tested: [colors.greenSoft, '#065F46'],
  'Tested late': ['#FEF9C3', '#854D0E'],
  Collected: ['#E0F2FE', '#075985'],
  'Form sent': [colors.amberSoft, '#92400E'],
  Pending: [colors.blueSoft, colors.blue],
  Missed: [colors.redSoft, '#991B1B'],
  Standby: [colors.graySoft, colors.textMuted],
  Excused: [colors.graySoft, colors.textMuted],
  'Not employed': [colors.graySoft, colors.textMuted],
};
const SUMMARY = ['Tested', 'Tested late', 'Collected', 'Form sent', 'Pending', 'Missed'];
// A DISA certificate is in: no disposition applies (the server's verdict ignores one).
const TESTED = ['Tested', 'Tested late', 'Collected'];

// The DISA authorization number; saves on blur (or Enter) when it changed.
function AuthInput({ value, disabled, onCommit }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <input style={{ ...styles.input, padding: '3px 6px', width: 120, marginTop: 4 }} placeholder="Authorization #"
      value={v} disabled={disabled} onChange={e => setV(e.target.value)}
      onBlur={() => { if (v.trim() !== (value || '')) onCommit(v.trim()); }}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

// "On job now" first, else the next scheduled job (the compliance digest's rule).
export function CurrentJob({ jobs }) {
  const j = (jobs || [])[0];
  if (!j) return <span style={{ fontSize: 12, color: colors.textMuted }}>Not on a job</span>;
  return (
    <div style={{ fontSize: 12 }}>
      <span style={{ ...styles.pill, fontSize: 10, background: j.kind === 'on_job' ? '#DCFCE7' : colors.blueSoft, color: j.kind === 'on_job' ? '#166534' : '#1E40AF' }}>
        {j.kind === 'on_job' ? 'ON JOB NOW' : 'SCHEDULED'}
      </span>
      {(j.shifts || []).map(s => (
        <span key={s} style={{ ...styles.pill, fontSize: 10, marginLeft: 4, background: /night/i.test(s) ? '#E0E7FF' : '#FEF3C7', color: /night/i.test(s) ? '#3730A3' : '#92400E' }}>
          {s.toUpperCase()} SHIFT
        </span>
      ))}
      <div style={{ fontWeight: 600, marginTop: 2 }}>{j.job}</div>
      {j.customer && <div style={{ color: colors.textMuted }}>{j.customer}{j.site && j.site !== j.job ? ` · ${j.site}` : ''}</div>}
      {j.crafts && j.crafts.length > 0 && <div style={{ color: colors.textMuted }}>{j.crafts.join(', ')}</div>}
      {j.kind === 'upcoming' && j.start && <div style={{ color: colors.textMuted }}>starts {fmtDate(j.start)}</div>}
      {jobs.length > 1 && <div style={{ color: colors.textMuted }}>+{jobs.length - 1} more</div>}
    </div>
  );
}

// The job(s) the person was on at the time: the collection day, else the period.
function JobsThen({ then }) {
  if (!then) return null;
  const label = then.on ? `ON ${fmtDate(then.on)}` : 'DURING THE PERIOD';
  return (
    <div style={{ fontSize: 12, marginBottom: 6 }}>
      <span style={{ ...styles.pill, fontSize: 10, background: colors.graySoft, color: colors.text }}>{label}</span>
      {then.jobs.length === 0 && <div style={{ color: colors.textMuted, marginTop: 2 }}>No job on record</div>}
      {then.jobs.map(j => (
        <div key={j.job} style={{ marginTop: 2 }}>
          <span style={{ fontWeight: 600 }}>{j.job}</span>
          {j.shifts.length > 0 && <span style={{ color: colors.textMuted }}> · {j.shifts.join('/')}</span>}
          {j.customer && <div style={{ color: colors.textMuted }}>{j.customer}</div>}
          {j.crafts.length > 0 && <div style={{ color: colors.textMuted }}>{j.crafts.join(', ')}</div>}
        </div>
      ))}
    </div>
  );
}

// DISA prints collection times as wall-clock text with no zone; show them as printed.
const fmtCollected = (s) => {
  if (!s) return '';
  const [d, t] = s.split('T');
  const [h, m] = (t || '').split(':').map(Number);
  if (Number.isNaN(h)) return fmtDate(d);
  return `${fmtDate(d)} ${(h % 12) || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};
const daysBetween = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);

function Collection({ label, c }) {
  if (!c) return null;
  return (
    <div style={{ fontSize: 12 }}>
      <b>{label}</b> {fmtCollected(c.collected_local)}
      <div style={{ color: colors.textMuted }}>{[c.collection_site, c.ccf_id].filter(Boolean).join(' · ')}</div>
      {c.other_policy && <div style={{ color: '#92400E' }}>collected under {c.policy}</div>}
    </div>
  );
}

export default function DisaRandomsTab() {
  const [period, setPeriod] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(null);   // a status: only rows in it are listed

  const load = (p = period) => api.disaRandomsList(p).then(d => { setData(d); setPeriod(d.period); setErr(''); }).catch(e => setErr(e.message));
  useEffect(() => { load(''); }, []);

  const act = async (fn, ok) => {
    setBusy(true); setMsg('');
    try { const r = await fn(); if (ok) setMsg(ok(r)); await load(); }
    catch (e) { setMsg(`⚠ ${e.message}`); }
    finally { setBusy(false); }
  };
  const save = (p, payload, ok) => act(() => api.disaRandomUpdate(p.id, payload), ok);
  const saveNotes = (p) => save(p, { notes: notes[p.id] ?? p.notes ?? '' }, () => {
    setNotes(n => { const c = { ...n }; delete c[p.id]; return c; });
    return `${p.first_name} ${p.last_name}: notes saved`;
  });
  const sync = () => act(() => api.disaRandomsSync(), r => r.ok
    ? (r.skipped ? 'Feed unchanged since the last load' : `Loaded ${r.groups} reports, ${r.people} people, ${r.certificates} certificates`)
    : `⚠ ${r.error}`);
  const download = (g) => api.disaRandomDownloadPdf(g.group_id, `DISA Random ${g.program} ${g.period_start}.pdf`).catch(e => setMsg(`⚠ ${e.message}`));

  if (err) return <div style={styles.errorBox}>{err}</div>;
  if (!data) return <div style={styles.card}>Loading DISA randoms…</div>;
  const s = data.sync || {};
  const selections = data.groups.flatMap(g => g.people.filter(p => p.kind === 'selection'));
  const usedReplacements = data.groups.flatMap(g => g.people.filter(p => p.kind === 'replacement' && p.status !== 'Standby'));
  const counts = SUMMARY.map(st => [st, [...selections, ...usedReplacements].filter(p => p.status === st).length]);

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, color: colors.charcoal }}>
            🎲 DISA Randoms <span style={{ color: colors.textMuted, fontWeight: 400 }}>· {period ? monthLabel(period) : ''}</span>
          </h2>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4, maxWidth: 820 }}>
            Mirrored from the DISA random notification emails in the OccMed folder. A test counts when a DISA Test Result
            Certificate with Test Purpose Random was collected inside the selection period. Confidential: do not tell an
            employee until their supervisor can send them straight to the collection site.
            {' '}Last loaded {s.last_ok_at ? fmtDateTime(s.last_ok_at) : 'never'}
            {s.report_modified ? ` · feed written ${fmtDateTime(s.report_modified)}` : ''}.
            {s.last_status === 'error' && <span style={{ color: colors.red, fontWeight: 600 }}> Last sync failed: {s.last_error}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={{ ...styles.input, width: 'auto' }} value={period} disabled={busy || !data.months.length}
            onChange={e => { setPeriod(e.target.value); setShow(null); load(e.target.value); }}>
            {!data.months.includes(period) && period && <option value={period}>{monthLabel(period)}</option>}
            {data.months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <button style={styles.btnSecondary} disabled={busy} onClick={sync}>⟳ Sync now</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
        <button onClick={() => setShow(null)} title="Show everyone"
          style={chipBtn(colors.graySoft, colors.charcoal, !show)}>
          Selected {selections.length}{usedReplacements.length ? ` + ${usedReplacements.length} replacement${usedReplacements.length === 1 ? '' : 's'} used` : ''}
        </button>
        {counts.filter(([, n]) => n).map(([st, n]) => (
          <button key={st} onClick={() => setShow(show === st ? null : st)} title={show === st ? 'Show everyone' : `Show only ${st}`}
            style={chipBtn(STATUS_COLORS[st][0], STATUS_COLORS[st][1], show === st)}>
            {st} {n}
          </button>
        ))}
        {show && <span style={{ alignSelf: 'center', color: colors.textMuted }}>Showing {show} only · click again for everyone</span>}
      </div>
      {msg && <div style={{ fontSize: 13, margin: '6px 0', color: msg.startsWith('⚠') ? colors.red : '#065F46' }}>{msg}</div>}
      {!data.groups.length && (
        <div style={{ color: colors.textMuted, padding: 12 }}>No DISA random reports loaded for this month yet. The OccMed saver runs hourly on the MinisForum.</div>
      )}
      {data.groups.map(g => {
        const left = g.period_end ? daysBetween(data.today, g.period_end) : null;
        const open = left != null && left >= 0 && data.today >= g.period_start;
        const rows = show ? g.people.filter(p => p.status === show) : g.people;
        if (show && !rows.length) return null;
        return (
          <div key={g.group_id} style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, color: colors.charcoal, fontSize: 15 }}>{g.program}</div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                  Period {fmtDate(g.period_start)} to {fmtDate(g.period_end)}
                  {open && <b style={{ color: left <= 5 ? colors.red : colors.charcoal }}> · {left === 0 ? 'last day' : `${left} day${left === 1 ? '' : 's'} left`}</b>}
                  {g.pool_size != null && ` · pool ${g.pool_size}`}
                  {g.drug_rate && ` · drug rate ${g.drug_rate}`}
                  {g.drug_required_ytd != null && ` (YTD ${g.drug_performed_ytd ?? '?'} of ${g.drug_required_ytd} performed)`}
                  {g.alcohol_rate && g.alcohol_rate !== '0%' && ` · alcohol rate ${g.alcohol_rate}`}
                  {g.alcohol_required_ytd ? ` (YTD ${g.alcohol_performed_ytd ?? '?'} of ${g.alcohol_required_ytd})` : ''}
                </div>
                {g.policy_name && <div style={{ fontSize: 11, color: colors.textMuted }}>{g.policy_name} · group {g.group_id} · received {fmtDateTime(g.received_at)}</div>}
              </div>
              {g.has_pdf && <button style={styles.btnSecondary} onClick={() => download(g)}>📄 DISA report</button>}
            </div>
            {!rows.length
              ? <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 8 }}>No selections this period.</div>
              : (
                <div style={{ overflowX: 'auto', marginTop: 8 }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {['Employee', 'SSN', 'Test For', 'Job (then / now)', 'Status', 'Collected', 'DISA Form Sent', 'Disposition', 'Notes'].map(h => <th key={h} style={styles.th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p, i) => {
                        const [bg, fg] = STATUS_COLORS[p.status] || STATUS_COLORS.Pending;
                        const firstRepl = p.kind === 'replacement' && (i === 0 || rows[i - 1].kind !== 'replacement');
                        return [
                          firstRepl && (
                            <tr key={`r-${p.id}`}><td colSpan={9} style={{ ...styles.td, fontSize: 11, fontWeight: 700, color: colors.textMuted, background: colors.graySoft }}>
                              REPLACEMENTS: use only when a primary selection cannot test this period
                            </td></tr>
                          ),
                          <tr key={p.id} style={{ opacity: p.status === 'Standby' ? 0.7 : 1 }}>
                            <td style={styles.td}>
                              <div style={{ fontWeight: 600 }}>{p.first_name} {p.last_name}</div>
                              {p.employee_name
                                ? <div style={{ fontSize: 11, color: '#065F46' }}>Roster: {p.employee_name}</div>
                                : <div style={{ fontSize: 11, color: colors.textMuted }}>not matched to the Roster</div>}
                              {p.employee_phone && <div style={{ fontSize: 11 }}><a href={`tel:${p.employee_phone.replace(/[^\d+]/g, '')}`}>{p.employee_phone}</a></div>}
                              {p.employee_email && <div style={{ fontSize: 11, wordBreak: 'break-all' }}><a href={`mailto:${p.employee_email}`}>{p.employee_email}</a></div>}
                              {(p.supervisor || p.job_number) && <div style={{ fontSize: 11, color: colors.textMuted }}>{[p.supervisor, p.job_number].filter(Boolean).join(' · ')}</div>}
                            </td>
                            <td style={styles.td}>{p.ssn_last4 ? `…${p.ssn_last4}` : ''}</td>
                            <td style={{ ...styles.td, fontSize: 12 }}>{p.test_for}</td>
                            <td style={{ ...styles.td, minWidth: 150 }}>
                              {p.employee_id ? <><JobsThen then={p.jobs_then} /><CurrentJob jobs={p.jobs} /></> : <span style={{ fontSize: 12, color: colors.textMuted }}>—</span>}
                            </td>
                            <td style={styles.td}>
                              <span style={{ ...styles.pill, background: bg, color: fg, whiteSpace: 'nowrap' }}>{p.status}</span>
                              {p.warned_at && !['Tested', 'Collected'].includes(p.status) && <div style={{ fontSize: 11, color: '#92400E', marginTop: 3 }}>DISA no-show warning {fmtDate(p.warned_at)}</div>}
                              {p.expired_at && <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3 }}>DISA audit: expired {fmtDate(p.expired_at)}</div>}
                            </td>
                            <td style={styles.td}>
                              <Collection label="Drug" c={p.drug} />
                              <Collection label="Alcohol" c={p.alcohol} />
                              {p.status === 'Tested late' && <>
                                <Collection label="Drug (late)" c={p.late_drug} />
                                <Collection label="Alcohol (late)" c={p.late_alcohol} />
                              </>}
                            </td>
                            <td style={styles.td}>
                              {p.form_file && (
                                <div style={{ fontSize: 12, marginBottom: 4 }} title={`Found in Downloads: ${p.form_file.file_name || ''}`}>
                                  <b>{fmtCollected(p.form_file.created_local)}</b>
                                  <div style={{ color: colors.textMuted }}>#{p.form_file.auth_number}{p.form_file.collection_site ? ` · ${p.form_file.collection_site}` : ''}</div>
                                </div>
                              )}
                              {(!p.form_file || p.form_sent_on || p.disa_auth_number) ? (
                                <>
                                  <BlurDateInput style={{ ...styles.input, padding: '3px 6px', width: 'auto' }} disabled={busy} value={p.form_sent_on}
                                    title="Date the DISA authorization form was sent to the employee"
                                    onCommit={v => save(p, { form_sent_on: v })} />
                                  <div><AuthInput value={p.disa_auth_number} disabled={busy} onCommit={v => save(p, { disa_auth_number: v })} /></div>
                                </>
                              ) : (
                                <details style={{ fontSize: 11, color: colors.textMuted }}>
                                  <summary style={{ cursor: 'pointer' }}>Enter by hand</summary>
                                  <BlurDateInput style={{ ...styles.input, padding: '3px 6px', width: 'auto' }} disabled={busy} value={p.form_sent_on}
                                    title="Date the DISA authorization form was sent to the employee"
                                    onCommit={v => save(p, { form_sent_on: v })} />
                                  <div><AuthInput value={p.disa_auth_number} disabled={busy} onCommit={v => save(p, { disa_auth_number: v })} /></div>
                                </details>
                              )}
                            </td>
                            <td style={styles.td}>
                              {TESTED.includes(p.status) ? (
                                <span style={{ fontSize: 12, color: colors.textMuted }} title="DISA's certificate is in, so no exception applies">Not needed</span>
                              ) : (
                                <select style={{ ...styles.input, padding: '3px 6px', width: 'auto' }} disabled={busy} value={p.disposition || ''}
                                  onChange={e => save(p, { disposition: e.target.value || null })} title="Not employed applies to this person on every DISA tab (Randoms, Annual, Roster Changes), every month">
                                  <option value="">—</option>
                                  {data.dispositions.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                              )}
                            </td>
                            <td style={{ ...styles.td, minWidth: 180 }}>
                              <textarea style={{ ...styles.textarea, minHeight: 34, fontSize: 12 }} value={notes[p.id] ?? p.notes ?? ''}
                                onChange={e => setNotes(n => ({ ...n, [p.id]: e.target.value }))} />
                              {notes[p.id] !== undefined && (
                                <button style={{ ...styles.btnSecondary, padding: '2px 8px', fontSize: 12 }} disabled={busy} onClick={() => saveNotes(p)}>Save notes •</button>
                              )}
                              {p.updated_at && <div style={{ fontSize: 10, color: colors.textMuted }}>edited {fmtDateTime(p.updated_at)}{p.updated_by_name ? ` by ${p.updated_by_name}` : ''}</div>}
                            </td>
                          </tr>,
                        ];
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
}
